import React, { FunctionComponent } from 'react';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles';

import { fetch_one } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';

import {
  Box,
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, TooltipProps
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

import { Typography } from '../../../../components/v2/styled';

import { User } from '../../../../models/User';

const { DateTime } = require("luxon");

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

type WakeStatPoint = {
  name: string;
  wake?: string;
  sleep?: string;
  wake_string?: string;
  sleep_string?: string;
  goal?: number;
}

type Props = {
  object: User;
}

export const CoachUserWidgetsBasicSleep: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);

  const statDay = DateTime.now().toFormat("dd/LL/y");
  const statRange = 30;

  const {
    data: wakeStats,
  } = useQuery({
    queryKey: ["wake_stats", object?.id, statDay, statRange],
    queryFn: () =>
      fetch_one<{custom_result: {data: WakeStatPoint[]}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        {
          object: 'user',
          class_action: 'custom_api_wake_stats',
          id: object?.id,
          day: statDay,
          range: statRange
        },
        login
      ),
    select: (d) => {
      return d.custom_result.data;
    },
    enabled: !!object?.id,
  });

  const stats = wakeStats || [];
  const points = stats.filter((p) => !!p.sleep || !!p.wake);
  const lastEntry = HelpersClientWidgets.getBasicSleepLastEntry(stats);

  const CustomTooltip = ({
    active,
    payload,
  }: TooltipProps<ValueType, NameType>) => {
    if (active && (payload?.length || 0) > 0) {
      const pl = (payload || [])[0].payload as WakeStatPoint;
      return (
        <div className="custom-tooltip" style={{
          textAlign: 'left',
          backgroundColor: 'white',
          border: '1px solid transparent',
          borderColor: theme.palette.primary.main,
          borderRadius: 8,
          padding: 8
        }}>
          <p className="label">{`${pl.name}`}</p>
          <p className="label">{`${t("coach.diary.sleep_at")} : ${pl.sleep_string}`}</p>
          <p className="label">{`${t("coach.diary.wake_at")} : ${pl.wake_string}`}</p>
        </div>
      );
    }

    return null;
  };

  return <Paper sx={{padding: 2, marginTop: 1}}>
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.basic_sleep")}</Typography>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2">{t("shared.terms.last_entry")}</Typography>
        {!lastEntry && <Typography variant="body2" sx={{fontStyle: 'italic'}}>{t("shared.terms.no_results")}</Typography>}
        {!!lastEntry && <Typography variant="body2">{DateTime.fromFormat(lastEntry.day, "dd/LL/yyyy").toFormat("DDD", {locale: i18n.resolvedLanguage || 'nl'})}</Typography>}
      </Grid>

      <Grid item xs={12}>
        <Box sx={{textAlign: 'center'}}>
          <Typography sx={{textDecoration: 'underline'}}>{t("shared.terms.last_30_days")}</Typography>
        </Box>
        {points.length < 1 && <Box sx={{textAlign: 'center'}}>
          <Typography sx={{fontStyle: 'italic'}}>{t("shared.terms.no_results")}</Typography>
        </Box>}
        {points.length > 0 && <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={stats}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 40,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-30} textAnchor="end" />
            <YAxis tick={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="top" />
            <Area type="monotone" connectNulls stackId="1" dataKey="sleep" stroke={theme.palette.primary.dark} fill="white" activeDot={{ r: 8 }} dot />
            <Area type="monotone" connectNulls stackId="1" dataKey="wake" stroke={theme.palette.primary.main} fill={theme.palette.primary.dark} activeDot={{ r: 8 }} dot />
          </AreaChart>
        </ResponsiveContainer>}
      </Grid>
    </Grid>
  </Paper>;
}
